import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, Fab, Badge } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { MessageBubble } from './MessageBubble';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

const isUnreadInbound = (m) =>
  m.type === 'customer' && m.status !== 'Visto';

export const ChatTranscript = ({
  messages = [],
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  onReply,
  onEdit,
  onForward,
  onDelete,
  emptyLabel = 'Sin mensajes aún',
}) => {
  const messagesById = useMemo(() => {
    const m = new Map();
    for (const msg of messages) m.set(String(msg.id), msg);
    return m;
  }, [messages]);

  // Detect first unread inbound for the divider. Captured once per conversation
  // so the divider stays put even after the messages are marked read.
  const initialUnreadIndexRef = useRef(null);
  useEffect(() => {
    if (initialUnreadIndexRef.current !== null) return;
    const idx = messages.findIndex(isUnreadInbound);
    if (idx >= 0) initialUnreadIndexRef.current = idx;
  }, [messages]);

  const unreadCount = useMemo(
    () => messages.filter(isUnreadInbound).length,
    [messages]
  );

  const handleJumpToQuoted = React.useCallback((quotedId) => {
    const el = document.querySelector(`[data-mid="${String(quotedId)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'background-color .2s';
      el.style.backgroundColor = 'rgba(65,133,241,0.10)';
      setTimeout(() => { el.style.backgroundColor = ''; }, 1200);
    }
  }, []);

  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const [showJump, setShowJump] = useState(false);
  const [pendingNew, setPendingNew] = useState(0);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setPendingNew(0);
  };

  const isNearBottom = () => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 200;
  };

  useEffect(() => {
    const lastId = messages.length > 0 ? messages[messages.length - 1]?.id : null;
    if (lastId && lastMessageIdRef.current && lastMessageIdRef.current !== lastId) {
      if (isNearBottom()) {
        scrollToBottom();
      } else {
        setPendingNew((n) => n + 1);
      }
    }
    lastMessageIdRef.current = lastId;
  }, [messages]);

  const handleScroll = () => {
    setShowJump(!isNearBottom());
  };

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 0,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: 2,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'grey.300', borderRadius: '4px' },
        }}
      >
        {hasMore && typeof onLoadOlder === 'function' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onLoadOlder}
              disabled={loadingOlder}
              sx={{ textTransform: 'none', borderRadius: 1 }}
            >
              {loadingOlder ? 'Cargando...' : 'Cargar anteriores'}
            </Button>
          </Box>
        )}
        {messages.map((message, index) => {
          const currentDate = new Date(message.timestamp);
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const showDateSeparator =
            !previousMessage ||
            !isSameDay(currentDate, new Date(previousMessage.timestamp));
          const showUnreadDivider =
            initialUnreadIndexRef.current === index && unreadCount > 0;

          return (
            <React.Fragment key={message.id}>
              {showDateSeparator && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'grey.200' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {format(currentDate, 'PPP', { locale: es })}
                    </Typography>
                  </Box>
                </Box>
              )}
              {showUnreadDivider && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                  <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(65,133,241,0.4)' }} />
                  <Typography variant="caption" sx={{ color: '#4185F1', fontWeight: 700, letterSpacing: '0.04em' }}>
                    {unreadCount} mensaje{unreadCount === 1 ? '' : 's'} sin leer
                  </Typography>
                  <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(65,133,241,0.4)' }} />
                </Box>
              )}
              <MessageBubble
                message={message}
                onReply={onReply}
                onEdit={onEdit}
                onForward={onForward}
                onDelete={onDelete}
                quotedMessage={message.replyToId ? messagesById.get(String(message.replyToId)) : null}
                onJumpToQuoted={handleJumpToQuoted}
              />
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {showJump && (
        <Badge
          badgeContent={pendingNew}
          color="primary"
          sx={{ position: 'absolute', bottom: 16, right: 16 }}
          invisible={pendingNew === 0}
        >
          <Fab
            size="small"
            onClick={() => scrollToBottom()}
            sx={{ bgcolor: '#fff', color: '#121826', boxShadow: 3, '&:hover': { bgcolor: '#f6f8fb' } }}
          >
            <KeyboardArrowDownIcon />
          </Fab>
        </Badge>
      )}
    </Box>
  );
};
